import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Chip,
  Autocomplete,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import RestoreIcon from "@mui/icons-material/Restore";
import request from "../../../utilities/requests";
import { toast } from "../../../utilities/SnackBarUtils";

const LeagueTeams = ({ eventId, leagueEvent, onLeagueEventUpdate }) => {
  const [teams, setTeams] = useState(leagueEvent?.teams || []);
  const [orgTeams, setOrgTeams] = useState([]);
  const [addTeamOpen, setAddTeamOpen] = useState(false);
  const [newTeamDialogOpen, setNewTeamDialogOpen] = useState(false);
  const [addPlayerOpen, setAddPlayerOpen] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [selectedTeamEntry, setSelectedTeamEntry] = useState(null);

  // Add existing team to league
  const [selectedOrgTeam, setSelectedOrgTeam] = useState(null);
  const [division, setDivision] = useState("");

  // Create new team
  const [newTeamName, setNewTeamName] = useState("");
  const [newTeamColor, setNewTeamColor] = useState("");

  // Add player
  const [playerOptions, setPlayerOptions] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  // Remove team confirm
  const [removeTeamOpen, setRemoveTeamOpen] = useState(false);
  const [removeTeamEntry, setRemoveTeamEntry] = useState(null);

  // Edit team
  const [editTeamOpen, setEditTeamOpen] = useState(false);
  const [editTeamEntry, setEditTeamEntry] = useState(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");
  const [editDivision, setEditDivision] = useState("");

  const leagueEventId = leagueEvent?._id;

  useEffect(() => {
    setTeams(leagueEvent?.teams || []);
  }, [leagueEvent]);

  useEffect(() => {
    getOrgTeams();
  }, []);

  const getOrgTeams = async () => {
    const result = await request("sportsteam");
    if (result?.success) setOrgTeams(result.data);
  };

  const refreshLeagueEvent = async () => {
    const result = await request(`leagueevent?eventId=${eventId}`);
    if (result?.success) {
      setTeams(result.data?.teams || []);
      onLeagueEventUpdate(result.data);
    }
  };

  // Load all attendees once when the dialog opens
  useEffect(() => {
    if (!addPlayerOpen) return;
    setSearchLoading(true);
    request(`leagueevent/attendees?eventId=${eventId}&limit=200`).then((result) => {
      if (result?.success) {
        setPlayerOptions(
          (result.attendees || []).map((a) => ({
            _id: a.userId,
            email: a.email,
            profile: a.profile,
          }))
        );
      }
      setSearchLoading(false);
    });
  }, [addPlayerOpen, eventId]);

  const handleAddExistingTeam = async () => {
    if (!selectedOrgTeam) return;
    const result = await request("sportsteam/addtoleague", "post", {
      leagueEventId,
      teamId: selectedOrgTeam._id,
      division: division || undefined,
    });
    if (result?.success) {
      toast.success("Team added to league");
      setAddTeamOpen(false);
      setSelectedOrgTeam(null);
      setDivision("");
      await refreshLeagueEvent();
    }
  };

  const handleCreateNewTeam = async () => {
    if (!newTeamName.trim()) return;
    const createResult = await request("sportsteam/create", "post", {
      name: newTeamName.trim(),
      color: newTeamColor || undefined,
    });
    if (!createResult?.success) return;

    const addResult = await request("sportsteam/addtoleague", "post", {
      leagueEventId,
      teamId: createResult.data._id,
      division: division || undefined,
    });
    if (addResult?.success) {
      toast.success("Team created and added to league");
      setNewTeamDialogOpen(false);
      setNewTeamName("");
      setNewTeamColor("");
      setDivision("");
      await getOrgTeams();
      await refreshLeagueEvent();
    }
  };

  const openRemoveTeam = (entry) => {
    setRemoveTeamEntry(entry);
    setRemoveTeamOpen(true);
  };

  const handleRemoveTeam = async () => {
    const result = await request("sportsteam/removefromleague", "post", {
      leagueEventId,
      teamId: removeTeamEntry.team._id,
    });
    if (result?.success) {
      toast.success("Team removed");
      setRemoveTeamOpen(false);
      setRemoveTeamEntry(null);
      await refreshLeagueEvent();
    }
  };

  const openEditTeam = (entry) => {
    setEditTeamEntry(entry);
    setEditName(entry.team?.name || "");
    setEditColor(entry.team?.color || "");
    setEditDivision(entry.division || "");
    setEditTeamOpen(true);
  };

  const handleEditTeam = async () => {
    if (!editName.trim()) return;
    // Update name/color on the org-level team
    const updateResult = await request("sportsteam/update", "post", {
      teamId: editTeamEntry.team._id,
      name: editName.trim(),
      color: editColor || undefined,
    });
    if (!updateResult?.success) return;

    // Update division on the league entry
    const divResult = await request("sportsteam/updatedivision", "post", {
      leagueEventId,
      teamId: editTeamEntry.team._id,
      division: editDivision || undefined,
    });
    if (divResult?.success) {
      toast.success("Team updated");
      setEditTeamOpen(false);
      setEditTeamEntry(null);
      await refreshLeagueEvent();
    }
  };

  const handleAddPlayer = async () => {
    if (!selectedPlayer || !selectedTeamId) return;
    const result = await request("sportsteam/addplayer", "post", {
      leagueEventId,
      teamId: selectedTeamId,
      userId: selectedPlayer._id,
    });
    if (result?.success) {
      toast.success("Player added to roster");
      setAddPlayerOpen(false);
      setSelectedPlayer(null);
      await refreshLeagueEvent();
    }
  };

  const handleRemovePlayer = async (teamId, userId) => {
    const result = await request("sportsteam/removeplayer", "post", {
      leagueEventId,
      teamId,
      userId,
    });
    if (result?.success) {
      toast.success("Player removed");
      await refreshLeagueEvent();
    }
  };

  const handleDefaultRoster = async (teamId) => {
    const result = await request("sportsteam/defaultroster", "post", {
      leagueEventId,
      teamId,
    });
    if (result?.success) {
      toast.success(`${result.added} player(s) copied from previous season`);
      await refreshLeagueEvent();
    }
  };

  const openAddPlayer = (teamEntry) => {
    setSelectedTeamId(teamEntry.team._id);
    setSelectedTeamEntry(teamEntry);
    setAddPlayerOpen(true);
  };

  // Filter out teams already in this league from the org team picker
  const leagueTeamIds = teams.map((t) => t.team?._id?.toString());
  const availableOrgTeams = orgTeams.filter((t) => !leagueTeamIds.includes(t._id?.toString()));

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">Teams</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setAddTeamOpen(true)}>
            Add Existing Team
          </Button>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setNewTeamDialogOpen(true)}>
            Create New Team
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {teams.map((entry) => (
          <Grid item xs={12} sm={6} md={4} key={entry.team?._id}>
            <Card variant="outlined">
              <CardHeader
                title={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="subtitle1">{entry.team?.name}</Typography>
                    {entry.team?.color && (
                      <Box sx={{ width: 14, height: 14, borderRadius: "50%", backgroundColor: entry.team.color, border: "1px solid #ccc" }} />
                    )}
                  </Box>
                }
                subheader={entry.division ? `Division: ${entry.division}` : "No division"}
                action={
                  <Box sx={{ display: "flex" }}>
                    <Tooltip title="Edit team">
                      <IconButton size="small" onClick={() => openEditTeam(entry)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove from league">
                      <IconButton size="small" onClick={() => openRemoveTeam(entry)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              />
              <CardContent sx={{ pt: 0 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {entry.roster?.length || 0} player{entry.roster?.length !== 1 ? "s" : ""}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Tooltip title="Copy roster from previous season">
                      <IconButton size="small" onClick={() => handleDefaultRoster(entry.team?._id)}>
                        <RestoreIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Add player">
                      <IconButton size="small" onClick={() => openAddPlayer(entry)} color="primary">
                        <PersonAddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(entry.roster || []).map((player) => (
                    <Chip
                      key={player._id || player}
                      label={player.profile?.firstName ? `${player.profile.firstName} ${player.profile.lastName}` : (player.email || player.toString())}
                      size="small"
                      onDelete={() => handleRemovePlayer(entry.team?._id, player._id || player)}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
        {teams.length === 0 && (
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              No teams added yet.
            </Typography>
          </Grid>
        )}
      </Grid>

      {/* Add Existing Team Dialog */}
      <Dialog open={addTeamOpen} onClose={() => setAddTeamOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Existing Team</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={availableOrgTeams}
            getOptionLabel={(t) => t.name}
            value={selectedOrgTeam}
            onChange={(_, v) => setSelectedOrgTeam(v)}
            renderInput={(params) => <TextField {...params} label="Select Team" sx={{ mt: 1 }} />}
          />
          <TextField
            label="Division (optional)"
            value={division}
            onChange={(e) => setDivision(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddTeamOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddExistingTeam} disabled={!selectedOrgTeam}>Add</Button>
        </DialogActions>
      </Dialog>

      {/* Create New Team Dialog */}
      <Dialog open={newTeamDialogOpen} onClose={() => setNewTeamDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Team</DialogTitle>
        <DialogContent>
          <TextField
            label="Team Name *"
            value={newTeamName}
            onChange={(e) => setNewTeamName(e.target.value)}
            fullWidth
            sx={{ mt: 1 }}
          />
          <TextField
            label="Team Color (optional)"
            value={newTeamColor}
            onChange={(e) => setNewTeamColor(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
            placeholder="#ff0000 or red"
          />
          <TextField
            label="Division (optional)"
            value={division}
            onChange={(e) => setDivision(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewTeamDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateNewTeam} disabled={!newTeamName.trim()}>Create & Add</Button>
        </DialogActions>
      </Dialog>

      {/* Remove Team Confirm Dialog */}
      <Dialog open={removeTeamOpen} onClose={() => setRemoveTeamOpen(false)}>
        <DialogTitle>Remove team from league?</DialogTitle>
        <DialogContent>
          <Typography>
            Remove <strong>{removeTeamEntry?.team?.name}</strong> from this league? Their roster assignment for this season will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveTeamOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleRemoveTeam}>Remove</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Team Dialog */}
      <Dialog open={editTeamOpen} onClose={() => setEditTeamOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Team</DialogTitle>
        <DialogContent>
          <TextField
            label="Team Name *"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            fullWidth
            sx={{ mt: 1 }}
          />
          <TextField
            label="Team Color (optional)"
            value={editColor}
            onChange={(e) => setEditColor(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
            placeholder="#ff0000 or red"
            InputProps={{
              startAdornment: editColor ? (
                <Box sx={{ width: 16, height: 16, borderRadius: "50%", backgroundColor: editColor, border: "1px solid #ccc", mr: 1, flexShrink: 0 }} />
              ) : null,
            }}
          />
          <TextField
            label="Division (optional)"
            value={editDivision}
            onChange={(e) => setEditDivision(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTeamOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditTeam} disabled={!editName.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Add Player Dialog */}
      <Dialog open={addPlayerOpen} onClose={() => setAddPlayerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Player to {selectedTeamEntry?.team?.name}</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={playerOptions}
            getOptionLabel={(u) => `${u.profile?.firstName || ""} ${u.profile?.lastName || ""} (${u.email})`}
            loading={searchLoading}
            value={selectedPlayer}
            onChange={(_, v) => setSelectedPlayer(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search attendees"
                sx={{ mt: 1 }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddPlayerOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddPlayer} disabled={!selectedPlayer}>Add Player</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeagueTeams;
